import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { loadLeaflet } from '../../utils/geo';
import { addBaseLayer } from '../../utils/offline/baseLayer';
import { tooltipText } from '../../utils/tooltipText';
import { setActiveTarget } from '../../utils/activeTarget';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { formatDuration } from '../../utils/audioNotes';
import { photoTypeLabel } from '../../utils/photoCapture';
import PhotoViewer from '../PhotoViewer';
import * as S from '../specimens/styles';

/*
 * What a chat message carries (routes/chat.py, migration 0093), as its
 * reader may see it:
 *
 *   photographs  a grid of thumbnails; one opens PhotoViewer (pinch, pan,
 *                previous and next through the message's photographs)
 *   voice notes  an <audio> player with the note's length, marked when it
 *                came from a walkie-talkie (recorded_via ptt_*)
 *   places       a small map that is only a picture (the base map works
 *                offline from what this device kept), the label as text --
 *                never markup: Leaflet sets a string as innerHTML, so the
 *                label goes in through tooltipText -- the coordinates, and
 *                Open in map / Set as target (field navigation)
 *   records      a card of what this reader may see of it, opening the
 *                record, or "Not visible to you"
 *
 * The URLs are the server's one-hour signed ones (chat/ is private); a
 * desktop's are re-based onto the server this page talks to.
 */
export default function MessageAttachments({ attachments = [], me }) {
  const images = attachments.filter((a) => a.kind === 'image');
  const others = attachments.filter((a) => a.kind !== 'image');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="chat-attachments">
      {images.length > 0 && <PhotoGrid images={images} />}
      {others.map((a) => {
        if (a.kind === 'audio') return <VoiceNote key={a.attachment_id} note={a} />;
        if (a.kind === 'location') return <PlaceCard key={a.attachment_id} place={a} me={me} />;
        return <RecordCard key={a.attachment_id} record={a.record} />;
      })}
    </div>
  );
}

function PhotoGrid({ images }) {
  const { t } = useTranslation('chat');
  const [open, setOpen] = useState(null);
  const photos = images.map((img) => ({ s3_url: img.url, thumbnail_url: img.thumb_url || img.url }));
  return (
    <>
      <ul style={{ ...grid, gridTemplateColumns: `repeat(${Math.min(images.length, 3)}, minmax(0, 1fr))` }}
        data-testid="chat-photos">
        {images.map((img, i) => (
          <li key={img.attachment_id}>
            <button type="button" style={thumbButton} onClick={() => setOpen(i)} data-testid="chat-photo"
              aria-label={t('attach.openPhoto', { n: i + 1, count: images.length })}>
              {img.url ? (
                <img src={resolvePhotoUrl(img.thumb_url || img.url)} alt="" loading="lazy" style={thumbImage}
                  width={img.width || undefined} height={img.height || undefined} />
              ) : <span style={S.hint}>{t('attach.unavailable')}</span>}
            </button>
          </li>
        ))}
      </ul>
      {open !== null && (
        <PhotoViewer photos={photos} index={open} onIndex={setOpen} onClose={() => setOpen(null)}
          dialogLabel={t('attach.photos')} testId="chat-photo-viewer" />
      )}
    </>
  );
}

function VoiceNote({ note }) {
  const { t } = useTranslation('chat');
  const walkie = note.recorded_via === 'ptt_key' || note.recorded_via === 'ptt_button';
  return (
    <div style={row} data-testid="chat-voice">
      <span aria-hidden="true">{walkie ? '📻' : '🎙️'}</span>
      {note.url ? (
        <audio controls preload="metadata" src={resolvePhotoUrl(note.url)} style={{ flex: '1 1 200px', minWidth: 0, height: 36 }}
          aria-label={walkie ? t('attach.walkieNote') : t('attach.voiceNote')} />
      ) : <span style={S.hint}>{t('attach.unavailable')}</span>}
      {Number.isFinite(note.duration_s) && (
        <span style={S.hint} dir="ltr" data-testid="chat-voice-length">{formatDuration(note.duration_s)}</span>
      )}
      {walkie && <span style={S.hint}>{t('attach.walkie')}</span>}
    </div>
  );
}

function MiniMap({ lat, lng, label, accuracy }) {
  const box = useRef(null);
  useEffect(() => {
    let map = null;
    let gone = false;
    loadLeaflet().then((L) => {
      if (gone || !box.current) return;
      map = L.map(box.current, { zoomControl: false, dragging: false, scrollWheelZoom: false, touchZoom: false,
        doubleClickZoom: false, boxZoom: false, keyboard: false }).setView([lat, lng], 14);
      addBaseLayer(L, map);
      if (accuracy) L.circle([lat, lng], { radius: accuracy, weight: 1, color: '#1f6feb', fillOpacity: 0.12 }).addTo(map);
      const pin = L.circleMarker([lat, lng], { radius: 7, weight: 2, color: '#fff', fillColor: '#b3261e',
        fillOpacity: 1 }).addTo(map);
      if (label) pin.bindTooltip?.(tooltipText(label), { direction: 'top' });
    }).catch(() => { /* no map: the coordinates below still say where */ });
    return () => {
      gone = true;
      map?.remove();
    };
  }, [lat, lng, label, accuracy]);
  // A picture of the place: what it says is written beside it.
  return <div ref={box} aria-hidden="true" style={mapBox} data-testid="chat-place-map" />;
}

function PlaceCard({ place, me }) {
  const { t } = useTranslation('chat');
  const [targeted, setTargeted] = useState(false);
  const accuracy = Number.isFinite(place.accuracy_m) ? Math.round(place.accuracy_m) : null;
  const target = () => {
    const made = setActiveTarget({ lat: place.lat, lng: place.lng, label: place.label || '', userId: me });
    setTargeted(!!made);
  };
  return (
    <div style={card} data-testid="chat-place">
      <MiniMap lat={place.lat} lng={place.lng} label={place.label} accuracy={accuracy} />
      <div style={{ padding: '6px 8px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {place.label && <strong dir="auto" data-testid="chat-place-label">{place.label}</strong>}
        <span style={S.hint}>
          <span dir="ltr">{place.lat.toFixed(5)}, {place.lng.toFixed(5)}</span>
          {accuracy !== null && <> · {t('attach.accuracy', { m: accuracy })}</>}
          {' · '}{t(`attach.source.${place.source}`, { defaultValue: place.source })}
        </span>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to="/map" state={{ focus: { lat: place.lat, lng: place.lng } }} style={linkButton}
            data-testid="chat-place-open">{t('attach.openInMap')}</Link>
          <button type="button" style={linkButton} onClick={target} data-testid="chat-place-target"
            title={t('attach.tip.target')}>{t('attach.setTarget')}</button>
        </div>
        {targeted && <span role="status" style={S.hint}>{t('attach.targetSet')}</span>}
      </div>
    </div>
  );
}

function RecordCard({ record }) {
  const { t } = useTranslation(['chat', 'common']);
  const [viewing, setViewing] = useState(false);
  if (!record || !record.visible) {
    return (
      <div style={{ ...card, padding: 8 }} data-testid="chat-record" data-visible="false">
        <span aria-hidden="true">🔒 </span>{t('attach.notVisible')}
      </div>
    );
  }
  const photo = record.type === 'photo';
  const thumb = record.thumbnail_url ? resolvePhotoUrl(record.thumbnail_url) : null;
  const what = photo ? photoTypeLabel(t, record.photo_type) || t('attach.aPhoto') : t('attach.aCollection');
  return (
    <div style={{ ...card, display: 'flex', gap: 8, padding: 8, alignItems: 'center' }} data-testid="chat-record"
      data-visible="true">
      {thumb && (photo ? (
        <button type="button" style={{ ...thumbButton, width: 64, flex: '0 0 64px' }} onClick={() => setViewing(true)}
          aria-label={t('attach.openPhoto', { n: 1, count: 1 })}>
          <img src={thumb} alt="" style={thumbImage} loading="lazy" />
        </button>
      ) : <img src={thumb} alt="" style={{ ...thumbImage, width: 64, height: 64, flex: '0 0 64px' }} loading="lazy" />)}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={S.hint}>{what}</span>
        {record.taxon_name && <em dir="auto">{record.taxon_name}</em>}
        {record.code && <span dir="ltr" style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13 }}>{record.code}</span>}
        {record.sample_id && (
          <Link to={`/collections?open=${encodeURIComponent(record.sample_id)}`} style={linkButton}
            data-testid="chat-record-open">{t('attach.openRecord')}</Link>
        )}
      </div>
      {viewing && (
        <PhotoViewer photos={[{ s3_url: record.url, thumbnail_url: record.thumbnail_url, photo_type: record.photo_type,
          taxon_name: record.taxon_name, record_code: record.code, captured_at: record.captured_at,
          capture_lat: record.capture_lat, capture_lng: record.capture_lng,
          capture_accuracy_m: record.capture_accuracy_m }]}
        index={0} onIndex={() => {}} onClose={() => setViewing(false)} dialogLabel={t('attach.aPhoto')} />
      )}
    </div>
  );
}

const grid = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4, maxWidth: 360 };
const thumbButton = {
  display: 'block', width: '100%', padding: 0, border: '1px solid var(--c-border)', borderRadius: 8,
  overflow: 'hidden', background: 'var(--c-bg-subtle)', cursor: 'zoom-in', aspectRatio: '1 / 1',
};
const thumbImage = { display: 'block', width: '100%', height: '100%', objectFit: 'cover' };
const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 220 };
const card = {
  border: '1px solid var(--c-border)', borderRadius: 10, overflow: 'hidden', background: 'var(--c-surface)',
  minWidth: 220, maxWidth: 360,
};
const mapBox = { height: 140, width: '100%', background: 'var(--c-bg-subtle)' };
const linkButton = {
  font: 'inherit', background: 'none', border: 'none', padding: 0, fontSize: 13, cursor: 'pointer',
  color: 'var(--c-primary-fg)', textDecoration: 'underline',
};
